import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Calendar, ClipboardList, Crown, AlertTriangle } from 'lucide-react';
import { tenantAdminApi } from '../../utils/tenantAdminApi';

function Stat({ icon: Icon, label, value, max, hint }) {
  const pct = max && max > 0 && value !== undefined ? Math.min(100, Math.round((value / max) * 100)) : null;
  const danger = pct !== null && pct >= 90;
  return (
    <div className="bg-white border rounded-xl p-5 flex items-start gap-4">
      <div className={`h-11 w-11 rounded-lg border flex items-center justify-center ${danger ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="text-[12px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="text-2xl font-bold text-zinc-900 mt-0.5">
          {value}
          {max && max > 0 && <span className="text-sm font-normal text-zinc-400"> / {max}</span>}
          {max === -1 && <span className="text-sm font-normal text-zinc-400"> · unlimited</span>}
        </div>
        {pct !== null && (
          <div className="mt-2 h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden">
            <div className={`h-full ${danger ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${pct}%` }} />
          </div>
        )}
        {hint && <div className="text-xs text-zinc-500 mt-1.5">{hint}</div>}
      </div>
    </div>
  );
}

export default function OverviewPage() {
  const [info, setInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, u] = await Promise.all([tenantAdminApi.tenant(), tenantAdminApi.usage()]);
        setInfo(t); setUsage(u);
      } catch (_) {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const c = usage?.counts || {};
  const l = usage?.limits || {};
  const limitReached = ['users', 'patients'].some(k => l[k] && l[k] > 0 && (c[k] || 0) >= l[k]);

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1
            className="text-2xl font-bold text-zinc-900"
            style={{ fontFamily: 'Manrope' }}
            data-testid="ta-overview-title"
          >
            Workspace overview
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {info?.name} · plan <b>{info?.plan?.name || info?.plan_code}</b> ·{' '}
            isolation <b>{info?.isolation_mode}</b>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm">
            <Crown className="h-4 w-4" />
            <span>
              Status:{' '}
              <b>{info?.subscription_status || info?.status}</b>
            </span>
          </div>
          <Link
            to="/admin/billing"
            className="text-sm font-medium text-blue-700 hover:underline"
            data-testid="ta-overview-upgrade-link"
          >
            Change plan →
          </Link>
        </div>
      </div>

      {limitReached && (
        <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3" data-testid="ta-overview-limit-warning">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            You've hit a plan limit. <Link to="/admin/billing" className="font-semibold underline">Upgrade your plan</Link> to keep adding records.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Users} label="Users" value={c.users} max={l.users} />
        <Stat icon={FileText} label="Patients" value={c.patients} max={l.patients} />
        <Stat icon={Calendar} label="Consultations" value={c.consultations} hint="Lifetime" />
        <Stat icon={ClipboardList} label="Templates" value={c.templates} hint="Custom" />
      </div>

      <div className="mt-8 bg-white border rounded-xl p-5">
        <h3 className="font-semibold text-zinc-900 mb-3">Plan features</h3>
        <ul className="space-y-1.5 text-sm text-zinc-700">
          {(info?.plan?.features || []).map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
